import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { fetchWalletBalance } from "../../Redux/Auth/AuthActions";
import { debounce } from "lodash";
import {
  checkWalletBalance,
  ResendOtpForSubmitTransfer,
  searchTransferTo,
  submitTransfer,
  submitTransferToAnotherOtp,
  viewEarningWalletApi,
  viewEmoneyTransferTypesApi,
} from "../../apis";
import PageTitle from "../../Components/UI/PageTitle";
import RowLoader from "../../Components/UI/RowLoader";
import Loader from "../../Components/Loader";
import { useNavigate } from "react-router-dom";

let timerInterval;
let countdown = 60;

const TransferEMoney = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [getDataLoader, setGetDataLoader] = useState(true);
  const [walletCheck, setWalletCheck] = useState(null);
  const [selectedName, setSelectedName] = useState("");
  const [transferTypes, setTransferTypes] = useState([]);
  const [selectedTransferType, setSelectedTransferType] = useState(null);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const walletBalance = useSelector((state) => state.Auth.walletBalance);
  const [userEarningWallet, setUserEarningWallet] = useState();

  useEffect(() => {
    setGetDataLoader(true);
    dispatch(fetchWalletBalance());
    getEmoneyTransferTypes();
    fetchEarningWallet().finally(() => {
      setGetDataLoader(false);
    });
  }, [dispatch]);

  const fetchEarningWallet = async () => {
    try {
      const response = await viewEarningWalletApi();
      setUserEarningWallet(response.data.data);
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
  };

  const handleTransferToChange = debounce(async (value) => {
    if (value.length > 0) {
      try {
        const results = await searchTransferTo(value);
        setSearchResults(results || []);
      } catch (error) {
        console.error("Error fetching search results:", error);
        setSearchResults([]);
      }
    } else {
      setSearchResults([]);
    }
  }, 500);

  const handleTransferValueChange = async (value, setFieldValue) => {
    let isBalanceSufficient = true;

    if (value) {
      const transferValue = Number(value);

      if (selectedTransferType === 3 || selectedTransferType === 10) {
        isBalanceSufficient = await checkWalletBalance(transferValue);
      } else if (selectedTransferType === 9 || selectedTransferType === 11) {
        const earningWalletBalance = Number(userEarningWallet);
        isBalanceSufficient = transferValue <= earningWalletBalance;
      }
    }

    setWalletCheck(isBalanceSufficient);
    setFieldValue("transfer_value", value);
  };

  const loggedInUserClientId = useSelector(
    (state) => state?.Auth?.loggedInUser?.data?.model?.id
  );

  const getEmoneyTransferTypes = async () => {
    try {
      const response = await viewEmoneyTransferTypesApi();
      setTransferTypes(response.data.data);
    } catch (error) {
      console.error("Error fetching transfer types:", error);
    }
  };
  const resendOtp = () => {
    ResendOtpForSubmitTransfer()
      .then((response) => {
        if (response.status === true) {
          showOtpModal(); // Re-open the modal
        }
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Resend Failed",
          text:
            error?.response?.data?.message?.[0] ||
            "Unable to resend OTP. Please try again.",
        });
      });
  };

  const showOtpModal = (resetForm) => {
    countdown = 60;

    Swal.fire({
      title: "Enter OTP",
      html: `
        <p style="margin-bottom: 10px; color: #4B5563; font-size: 14px;">
          The OTP has been sent to your email.
          <i class="material-icons">email</i>
        </p>
        <input id="otp-input" class="swal2-input" placeholder="Enter the 6-digit code" maxlength="6" />
        <p id="resend-timer" style="margin-top: 10px;">Resend OTP in <b>${countdown}</b>s</p>
      `,
      showCancelButton: true,
      confirmButtonText: "Verify",
      didOpen: () => {
        const content = Swal.getHtmlContainer();
        const $timer = content.querySelector("#resend-timer");

        timerInterval = setInterval(() => {
          countdown--;
          if (countdown <= 0) {
            clearInterval(timerInterval);
            $timer.innerHTML = `
              <button id="resend-btn" class="tw-bg-white tw-text-gray-800 tw-border tw-border-gray-300 tw-rounded tw-px-3 tw-py-1 tw-text-sm tw-inline-flex tw-items-center">
                <i class="material-icons tw-text-base tw-mr-1">refresh</i> Resend
              </button>
            `;
            document
              .getElementById("resend-btn")
              .addEventListener("click", () => {
                countdown = 60;
                resendOtp();
              });
          } else {
            $timer.innerHTML = `Resend OTP in <b>${countdown}</b>s`;
          }
        }, 1000);
      },
      preConfirm: () => {
        const otp = document.getElementById("otp-input").value;
        if (!otp) {
          Swal.showValidationMessage("OTP is required");
        }
        return otp;
      },
      willClose: () => {
        clearInterval(timerInterval);
      },
    }).then((result) => {
      if (result.isConfirmed) {
        const enteredOTP = result.value;
        submitTransferToAnotherOtp(enteredOTP)
          .then((response) => {
            Swal.fire({
              title: "Success",
              text:
                response.data.msg ||
                "E-Money Has Been Successfully Transferred",
              icon: "success",
            });
            dispatch(fetchWalletBalance());
            fetchEarningWallet();
            setSelectedTransferType(null);
            resetForm();
            setSelectedName("");
          })
          .catch((error) => {
            Swal.fire({
              title: "Error",
              text: error?.response?.data?.message?.[0] || "Wrong OTP",
              icon: "error",
            });
          });
      }
    });
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    countdown = 60;
    setLoading(true);
    try {
      const response = await submitTransfer(values);

      if (values.transfer_type === 3 || values.transfer_type === 9) {
        if (response.data.requireOtp) {
          showOtpModal(resetForm);
        } else {
          Swal.fire({
            title: "Error",
            text: "There was an error sending the request",
            icon: "error",
          });
        }
      } else {
        Swal.fire({
          title: "Success",
          text: "E-Money Has Been Successfully Transferred",
          icon: "success",
        });
      }
      dispatch(fetchWalletBalance());
      fetchEarningWallet();
      setSelectedTransferType(null);
      resetForm();
      setSelectedName("");
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: "There was an error sending the request",
        icon: "error",
      });
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  const validationSchema = Yup.object().shape({
    transfer_type: Yup.string().required("Transfer type is required"),
    transfer_to: Yup.string().required("Transfer to is required"),
    transfer_value: Yup.number()
      .required("Transfer value is required")
      .positive("Must be a positive number"),
    notes: Yup.string().required("Transfer reason is required"),
    confirm: Yup.boolean().oneOf([true], "Confirmation is required"),
  });
  if (getDataLoader) return <Loader />;
  return (
    <>
      <PageTitle title="Transfer E-Money" />
      <div className="tw-flex-col gap-6">
        <div className="card">
          <div className="card-header d-flex max-lg:!tw-flex-col max-lg:tw-gap-3 tw-text-center align-items-center justify-content-between">
            <h4 className="card-title mb-0">Transfer E-Money</h4>
            <div className="tw-flex max-sm:!tw-flex-col max-sm:tw-gap-3 tw-gap-6">
              <h5 className="tw-text-base tw-font-medium mb-0">
                Earning Wallet:{" "}
                <span className="tw-text-primary tw-font-bold">
                  {userEarningWallet} E.M
                </span>
              </h5>
              <h5 className="tw-text-base tw-font-medium mb-0">
                Registration Wallet:{" "}
                <span className="tw-text-primary tw-font-bold">
                  {walletBalance} E.M
                </span>
              </h5>
            </div>
          </div>
          <div className="card-body">
            <Formik
              initialValues={{
                transfer_type: "",
                transfer_to: "",
                transfer_value: "",
                notes: "",
                confirm: false,
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              validateOnChange={true} // This is the default value
              validateOnBlur={true} // This is the default value
            >
              {({
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                values,
              }) => {
                return (
                  <Form>
                    <div className="row">
                      <div className="tw-mb-6">
                        <h3 className="tw-text-lg tw-font-semibold tw-mb-2">
                          Select Transfer Type:
                        </h3>
                        <div className="tw-grid tw-grid-cols-1 tw-mb-6">
                          {transferTypes.map((type) => (
                            <label
                              key={type.id}
                              className="tw-flex tw-items-center tw-gap-3 tw-bg-white tw-shadow-md tw-p-3 tw-rounded-md tw-cursor-pointer hover:tw-bg-gray-100"
                            >
                              <Field
                                type="radio"
                                name="transfer_type"
                                value={type.id}
                                checked={selectedTransferType === type.id}
                                onChange={() => {
                                  setSelectedTransferType(type.id);
                                  setFieldValue("transfer_type", type.id);
                                  if (type.id === 10 || type.id === 11) {
                                    setFieldValue(
                                      "transfer_to",
                                      loggedInUserClientId.toString()
                                    );
                                  } else {
                                    setFieldValue("transfer_to", "");
                                  }
                                }}
                                className={`tw-h-5 tw-w-5`}
                              />
                              <span
                                className={`tw-text-sm tw-font-medium ${
                                  selectedTransferType === type.id
                                    ? "tw-text-primary"
                                    : ""
                                }`}
                              >
                                {type.transaction_type}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {!!selectedTransferType && (
                        <>
                          {selectedTransferType !== 10 &&
                            selectedTransferType !== 11 && (
                              <div className="col-md-6 mb-3">
                                <label
                                  className="form-label required-label"
                                  htmlFor="transfer_to"
                                >
                                  Transfer To
                                </label>
                                <Field
                                  type="text"
                                  name="transfer_to"
                                  className={`form-control ${
                                    touched.transfer_to &&
                                    errors.transfer_to &&
                                    !values.transfer_to
                                      ? "custom-invalid-input"
                                      : ""
                                  }`}
                                  value={selectedName}
                                  innerRef={inputRef}
                                  onChange={(e) => {
                                    const { value } = e.target;
                                    setSelectedName(value);
                                    handleTransferToChange(value);
                                    setFieldValue("transfer_to", "", true); // Reset value and trigger validation
                                  }}
                                />
                                {touched.transfer_to &&
                                  errors.transfer_to &&
                                  !values.transfer_to && (
                                    <ErrorMessage
                                      name="transfer_to"
                                      component="div"
                                      className="invalid-feedback"
                                    />
                                  )}

                                {searchResults.length > 0 && (
                                  <ul
                                    ref={dropdownRef}
                                    className="tw-absolute tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg tw-shadow-lg tw-w-full tw-mt-2 tw-max-h-48 tw-overflow-y-auto tw-z-10"
                                  >
                                    {searchResults.map((result) => (
                                      <li
                                        key={result.id}
                                        className="tw-list-none"
                                      >
                                        <button
                                          className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-cursor-pointer tw-hover:bg-gray-100"
                                          onClick={() => {
                                            setFieldValue(
                                              "transfer_to",
                                              result.id,
                                              true
                                            ); // Set value and trigger validation
                                            setFieldTouched(
                                              "transfer_to",
                                              true
                                            ); // Mark as touched
                                            setSelectedName(
                                              `${result.name} (${result.referral_code})`
                                            );
                                            setSearchResults([]);
                                          }}
                                        >
                                          {result.name} ({result.referral_code})
                                        </button>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}

                          <div className="col-md-6 mb-3">
                            <label
                              className="form-label required-label"
                              htmlFor="transfer_value"
                            >
                              Transfer Value (EM)
                            </label>
                            <Field
                              type="number"
                              name="transfer_value"
                              className={`form-control ${
                                touched.transfer_value && errors.transfer_value
                                  ? "custom-invalid-input"
                                  : ""
                              }`}
                              onChange={(e) => {
                                const { value } = e.target;
                                handleTransferValueChange(value, setFieldValue);
                              }}
                            />

                            <ErrorMessage
                              name="transfer_value"
                              component="div"
                              className="invalid-feedback"
                            />
                            {walletCheck === false && (
                              <div className="text-danger">
                                Insufficient Wallet balance.
                              </div>
                            )}
                          </div>

                          <div className="col-md-12">
                            <div className="col-md-6 mb-3 !tw-p-0">
                              <label
                                className="form-label required-label"
                                htmlFor="notes"
                              >
                                Transfer reason
                              </label>
                              <Field
                                as="textarea"
                                name="notes"
                                className="form-control"
                                rows="4"
                              />
                              <ErrorMessage
                                name="notes"
                                component="div"
                                className="invalid-feedback"
                              />
                            </div>
                          </div>

                          <div className="form-check mb-3 tw-flex tw-items-end tw-w-full tw-ms-2">
                            <input
                              type="checkbox"
                              className={`form-check-input tw-h-5 tw-w-5 ${
                                touched.confirm && errors.confirm
                                  ? "is-invalid"
                                  : ""
                              }`}
                              id="confirmCheckbox"
                              name="confirm"
                              checked={values.confirm}
                              onChange={(e) =>
                                setFieldValue("confirm", e.target.checked)
                              }
                            />
                            <label
                              className="form-check-label !tw-m-0 tw-ps-4 tw-w-full"
                              htmlFor="confirmCheckbox"
                            >
                              I confirm that the transfer process is correct,
                              and I can't cancel it.
                            </label>
                          </div>

                          <button
                            disabled={walletCheck === false}
                            type="submit"
                            className="btn btn-primary ml-auto mt-5 !tw-w-[100px]"
                          >
                            {loading ? (
                              <RowLoader paddingStyle="tw-py-0" />
                            ) : (
                              "Transfer"
                            )}
                          </button>
                        </>
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransferEMoney;
